import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useRBACStore } from '../../../stores/useRBACStore';
import { useTransition } from 'react';
import localStorageUtil from '../../../utils/localStorage';
import rightSideNav from './images/right-sidenav-arrow.svg';

import { Version } from '../../../utils/constants';

const MenuItem = ({ menu, isOpen, level = 0, onToggleSidebar }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const hasPrivilege = useRBACStore((s) => s.hasPrivilege);

  // Filter children based on privileges
  const visibleChildren =
    menu.children?.filter(
      (child) => !child.privilege || hasPrivilege(child.privilege)
    ) || [];

  const hasChildren = visibleChildren.length > 0;

  // Check if any child is active to determine if parent should be expanded
  const hasActiveChild =
    hasChildren &&
    visibleChildren.some((child) => {
      // Use activeMatch if provided, otherwise fallback to exact match
      if (child.activeMatch) {
        return child.activeMatch.some((pattern) => {
          if (pattern === '/') {
            return location.pathname === '/';
          }
          return new RegExp(pattern).test(location.pathname);
        });
      }
      return location.pathname === child.navigate;
    });

  // Use activeMatch if provided, otherwise fallback to existing logic
  const isActive = menu.activeMatch
    ? menu.activeMatch.some((pattern) => {
        if (pattern === '/') {
          return location.pathname === '/';
        }
        return new RegExp(pattern).test(location.pathname);
      })
    : location.pathname === menu.navigate ||
      (hasChildren &&
        visibleChildren.some((child) => location.pathname === child.navigate));

  // Auto-expand parent when child is active (only for the current parent)
  useEffect(() => {
    if (hasActiveChild && isOpen) {
      setIsExpanded(true);
    }
  }, [hasActiveChild, isOpen]);

  const handleClick = (e) => {
    if (hasChildren) {
      e.preventDefault();
      if (!isOpen) {
        // If sidebar is collapsed, expand it first
        onToggleSidebar();
        // Set a small delay to allow sidebar to expand before showing children
        setTimeout(() => setIsExpanded(true), 100);
      } else {
        // Only toggle the clicked parent's expansion state
        setIsExpanded(!isExpanded);
      }
    } else if (menu.navigate) {
      e.preventDefault();
      startTransition(() => {
        navigate(menu.navigate);
      });
    }
  };

  // Check if user has privilege to view this menu item
  if (menu.privilege && !hasPrivilege(menu.privilege)) {
    return null;
  }

  // If menu has children but no visible children (all children lack privileges), don't render
  if (
    menu.children &&
    menu.children.length > 0 &&
    visibleChildren.length === 0
  ) {
    return null;
  }

  // If menu has onClick, render a button instead of NavLink
  if (menu.onClick) {
    return (
      <button
        onClick={menu.onClick}
        className={`
            group flex cursor-pointer items-center w-full relative
            text-[#8E92BC] opacity-90
            ${menu.gap ? 'mt-9' : 'mt-2'}
            ${
              isOpen
                ? 'mx-2 py-2 px-3 rounded-md hover:bg-[#495560] hover:bg-opacity-50'
                : 'py-2 px-3 rounded-md hover:bg-[#495560] hover:bg-opacity-50 justify-center group'
            }
            ${level > 0 ? 'pl-8' : ''}
          `}
      >
        <div className="flex items-center justify-center w-6 h-6">
          {menu?.src}
        </div>
        {isOpen && (
          <div className="flex items-center justify-between flex-1">
            <span className="ml-3 truncate duration-300 whitespace-nowrap">
              {menu.title}
            </span>
          </div>
        )}
        {/* Tooltip for collapsed state */}
        {!isOpen && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-[#151C2E] text-[#8E92BC] text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
            {menu.title}
          </div>
        )}
      </button>
    );
  }

  return (
    <>
      <NavLink
        to={menu.navigate || '#'}
        className={`group text-[#EAEAEA] 
          flex cursor-pointer items-center w-full relative
          ${isActive ? 'text-[#EAEAEA]' : ''}
          ${menu.gap ? 'mt-9' : 'mt-2'}
          ${isActive ? 'bg-[#495560]' : 'opacity-90'}
          ${
            isOpen
              ? ' py-2 px-3 rounded-sm hover:bg-[#495560] hover:bg-opacity-50'
              : 'py-2 px-3 rounded-md hover:bg-[#495560] hover:bg-opacity-50 justify-center group'
          }
          ${isActive && isOpen ? 'border-l-4 border-[#FF9900]' : ''}
          ${level > 0 ? 'pl-8' : ''}
        `}
        onClick={handleClick}
      >
        <div className="flex items-center justify-center w-8 h-6">
          {menu?.src}
        </div>
        {isOpen && (
          <div className="flex items-center justify-between flex-1">
            <span className="ml-3 truncate duration-300 whitespace-nowrap">
              {menu.title}
            </span>
            {hasChildren && (
              <div className="flex items-center gap-1">
                {/* {hasActiveChild && (
                  <div className="w-1.5 h-1.5 bg-[#FF9900] rounded-full opacity-70"></div>
                )} */}
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            )}
          </div>
        )}
        {/* Tooltip for collapsed state */}
        {!isOpen && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-[#151C2E] text-[#8E92BC] text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
            {menu.title}
          </div>
        )}
      </NavLink>

      {hasChildren && isOpen && isExpanded && (
        <div className="flex flex-col">
          {visibleChildren.map((child, index) => (
            <NavLink
              key={index}
              to={child.navigate}
              end={child.end}
              onClick={(e) => {
                e.preventDefault();
                startTransition(() => {
                  navigate(child.navigate);
                });
              }}
              className={({ isActive }) =>
                [
                  'group flex cursor-pointer items-center relative mx-2 py-2 pl-8 rounded-md transition-all duration-200 hover:bg-[#495560] hover:bg-opacity-50',
                  isActive
                    ? 'text-[#FF9900] rounded-tr-none rounded-br-none mx-0 bg-[#FF9900] bg-opacity-10 ml-4 pl-6 font-medium'
                    : 'text-[#8E92BC] opacity-90',
                ].join(' ')
              }
            >
              <span className="truncate duration-300 whitespace-nowrap">
                {child.title}
              </span>
              {/* Tooltip for child items when sidebar is collapsed */}
              {!isOpen && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-[#151C2E] text-[#8E92BC] text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
                  {child.title}
                </div>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </>
  );
};

const SideNav = ({ menus }) => {
  const [isOpen, setIsOpen] = useState(() => {
    const savedState = sessionStorage.getItem('sidenavOpen');
    return savedState === null ? true : savedState === 'true';
  });
  const userDetails = localStorageUtil.getItem('userDetails');

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    sessionStorage.setItem('sidenavOpen', newState.toString());
  };

  return (
    <nav
      className={`${
        isOpen ? 'w-56' : 'w-16'
      } bg-[#151C2E] duration-300 flex flex-col h-screen overflow-x-hidden`}
    >
      {/* Header with Toggle Button */}
      <div className="flex h-18">
        <div
          className={`flex items-center w-full pt-1 ${
            isOpen ? 'px-2' : 'justify-center'
          }`}
        >
          <button
            type="button"
            onClick={handleToggle}
            className="flex items-center p-2"
          >
            <div className="flex items-center justify-center w-8 h-8">
              <img
                alt="toggle sidebar"
                src={rightSideNav}
                className={`w-full h-full duration-300 ${isOpen && 'rotate-180'}`}
              />
            </div>
          </button>
          {isOpen && (
            <span className="ml-3 text-[#ffefdb] font-medium text-xl capitalize truncate">
              Hi {userDetails?.name || userDetails?.username || ''}
            </span>
          )}
        </div>
      </div>

      {/* Navigation Menu */}
      <ul
        className="flex-1 overflow-y-auto overflow-x-hidden max-w-full 
    [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {menus?.map((menu, index) => (
          <li key={index} className="group">
            <MenuItem
              menu={menu}
              isOpen={isOpen}
              onToggleSidebar={handleToggle}
            />
          </li>
        ))}
      </ul>

      {/* Version Info */}
      {isOpen && (
        <div className="flex justify-center py-5">
          <span className="text-[#8E92BC] text-xs opacity-40">
            {Version}
          </span>
        </div>
      )}
    </nav>
  );
};

MenuItem.propTypes = {
  menu: PropTypes.shape({
    navigate: PropTypes.string,
    src: PropTypes.node,
    title: PropTypes.string,
    gap: PropTypes.bool,
    children: PropTypes.arrayOf(PropTypes.object),
    onClick: PropTypes.func,
    activeMatch: PropTypes.arrayOf(PropTypes.string), // Array of regex patterns for custom matching
    privilege: PropTypes.string,
  }),
  isOpen: PropTypes.bool.isRequired,
  level: PropTypes.number,
  onToggleSidebar: PropTypes.func.isRequired,
};

SideNav.propTypes = {
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      navigate: PropTypes.string,
      src: PropTypes.node,
      title: PropTypes.string,
      gap: PropTypes.bool,
      children: PropTypes.arrayOf(PropTypes.object),
      onClick: PropTypes.func,
      activeMatch: PropTypes.arrayOf(PropTypes.string), // Array of regex patterns for custom matching
      privilege: PropTypes.string,
    })
  ),
};

export default SideNav;
